import { Dispatch, memo, SetStateAction, useMemo, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import FiltersBar from './FiltersBar';
import { TimeSort } from './TimeSort';
import { ConversationList } from './ConversationList';
import { ConversationTodoFilter } from './todoUnprocessed';
import {
  useCategoryAwareFilterAdvance,
  useCategoryAwareSort,
  useSetCategoryAwareSort,
  useSetCategoryAwareTodoFilter,
  useCategoryAwareTodoFilter,
  useHydrateCategoryFilterState,
} from 'hooks/business/conversationsHooks/useCategoryFilterState';
import { useBatchForwardLoading } from 'hooks/business/conversationsHooks/useConversationBatchForward';
import { Spin, Tooltip } from 'antd';
import { QuestionCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { ConversationCategoryType } from 'hooks/business/conversationsHooks/useConversationCategory';
import { ConversationListRef } from './ConversationList';
import { RotateCw } from 'lucide-react';

interface ConversationListSectionProps {
  category: ConversationCategoryType;
  convWraperRef: HTMLDivElement | null;
  onForceUpdate: () => void;
  forceUpdateKey: number;
  onForward?: (conversation: Conversation.Conversation) => void;
  cacheConvIds: string[];
  setCacheConvIds: Dispatch<SetStateAction<string[]>>;
}

const SpinContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 999;
  background: rgba(35, 36, 38, 0.5);
`;

/**
 * 会话列表区块组件
 * 为 Mine 和 All 分类分别管理筛选状态和列表
 *
 * 注意：
 * - Mine 分类支持完整功能（筛选、时间排序、待办筛选）
 * - All 分类仅支持基础筛选，不支持时间排序和待办筛选
 */
export const ConversationListSection = memo(function ConversationListSection(
  props: ConversationListSectionProps,
) {
  const { category, onForceUpdate, forceUpdateKey, onForward, cacheConvIds, setCacheConvIds } =
    props;

  const { t } = useTranslate();
  const isMine = category === 'mine';
  const batchForwardLoading = useBatchForwardLoading();

  const conversationListRef = useRef<ConversationListRef>(null);

  // 使用分类感知的筛选状态
  useHydrateCategoryFilterState(category);
  const filterAdvance = useCategoryAwareFilterAdvance(category);
  const sort = useCategoryAwareSort(category);
  const todoFilter = useCategoryAwareTodoFilter(category);

  const setSort = useSetCategoryAwareSort();
  const setTodoFilter = useSetCategoryAwareTodoFilter();

  // 判断是否显示时间排序（仅 Mine 分类支持）
  const showTimeSort = useMemo(() => {
    if (!isMine) return false;
    const statusFilters = filterAdvance?.statusFilters;
    return statusFilters?.includes('IN_RECEPTION') || statusFilters?.includes('UNPROCESSED');
  }, [filterAdvance?.statusFilters, isMine]);

  // 判断是否显示待办筛选（仅 Mine 分类支持）
  const showTodoFilter = useMemo(() => {
    if (!isMine) return false;
    return filterAdvance?.statusFilters?.includes('IMPORTANT_TODO');
  }, [filterAdvance?.statusFilters, isMine]);

  return (
    <>
      {batchForwardLoading && (
        <SpinContainer>
          <Spin
            tip={t('scene.chatWorkspace.forwardDialog.feedback.forwarding')}
            spinning={batchForwardLoading}
          />
        </SpinContainer>
      )}

      <FiltersBar category={category} />

      {showTodoFilter && (
        <div className="flex justify-between p-2">
          <div
            className="flex items-center gap-[2px] text text-xs text-content-200 cursor-pointer hover:text-primary-hover"
            onClick={onForceUpdate}
          >
            <span>{t('common.action.refresh')}</span>
            <ReloadOutlined />
          </div>
          <div>
            <ConversationTodoFilter
              reload={onForceUpdate}
              value={todoFilter}
              onChange={(value) => {
                setTodoFilter(category, value);
                onForceUpdate();
              }}
            />
          </div>
        </div>
      )}

      {showTimeSort && (
        <TimeSort
          category={category}
          type={sort}
          onChange={(type) => {
            setSort(category, type);
            onForceUpdate();
          }}
          onRefresh={onForceUpdate}
        />
      )}

      {category === 'all' && (
        <div className="p-3 bg-[#FFFAF0] text-xs text-[#515359] flex items-center justify-between">
          <span>
            {t('scene.chatWorkspace.conversationList.allTab.tooltip.title')}
            <Tooltip
              placement="right"
              title={
                <div className="whitespace-pre-wrap">
                  {t('scene.chatWorkspace.conversationList.allTab.tooltip.description')}
                </div>
              }
            >
              <QuestionCircleOutlined className="ml-1" />
            </Tooltip>
          </span>
          <Tooltip title={t('scene.chatWorkspace.conversationList.allTab.tooltip.refresh')}>
            <RotateCw
              size={16}
              className="cursor-pointer"
              onClick={() => conversationListRef.current?.refresh()}
            />
          </Tooltip>
        </div>
      )}

      <ConversationList
        ref={conversationListRef}
        key={forceUpdateKey}
        category={category}
        sort={sort}
        filterAdvance={filterAdvance}
        todoFilter={todoFilter}
        onForward={onForward}
        cacheConvIds={cacheConvIds}
        setCacheConvIds={setCacheConvIds}
      />
    </>
  );
});

export default ConversationListSection;
